/* ============================================================
   VIMEO PLAYER LAYER (vimeo.js)
   ------------------------------------------------------------
   Thin wrapper around Vimeo's iframe postMessage API so the
   feed can play / pause / mute / seek without loading the full
   Vimeo Player SDK. If you prefer the official SDK later
   (https://player.vimeo.com/api/player.js), only this file
   needs to change — feed.js talks to VimeoPlayer's methods,
   never to the iframe directly.

   VIMEO INTEGRATION NOTES
   -----------------------
   • Swap placeholder IDs in js/data.js — this file is ID-agnostic.
   • For Vimeo Pro/Business "unlisted" videos, append the privacy
     hash to the URL: /video/{id}?h={hash}&...  (add an `h` field
     to the item in data.js and thread it through buildSrc()).
   • autopause=0 lets us manage exclusivity ourselves (the feed
     guarantees only the active slide plays).
   ============================================================ */

const VIMEO_ORIGIN = 'https://player.vimeo.com';

class VimeoPlayer {
  /**
   * @param {HTMLElement} mount  container to inject the iframe into
   * @param {string} vimeoId    the (placeholder) Vimeo video ID
   * @param {object} callbacks  { onTimeUpdate({seconds,percent,duration}), onPlay(), onPause() }
   */
  constructor(mount, vimeoId, callbacks = {}) {
    this.mount = mount;
    this.vimeoId = vimeoId;
    this.callbacks = callbacks;
    this.ready = false;
    this.queued = [];        // commands issued before the player is ready
    this.duration = 0;
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'video-iframe';
    this.iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    this.iframe.setAttribute('frameborder', '0');
    this.iframe.setAttribute('title', 'video player');
    this.iframe.src = this.buildSrc();
    mount.appendChild(this.iframe);

    this._onMessage = this._onMessage.bind(this);
    window.addEventListener('message', this._onMessage);
  }

  buildSrc() {
    // background-style chrome: no controls (the feed draws its own),
    // muted by default so mobile autoplay is allowed, loop for clips.
    const params = new URLSearchParams({
      autopause: '0',
      autoplay: '0',
      muted: '1',
      loop: '1',
      controls: '0',
      playsinline: '1',
      dnt: '1',
      title: '0',
      byline: '0',
      portrait: '0',
      app_id: '122963', // generic embed app id used by player.js
    });
    return `${VIMEO_ORIGIN}/video/${this.vimeoId}?${params}`;
  }

  /* ---- postMessage plumbing ---- */
  _send(method, value) {
    if (!this.ready) { this.queued.push([method, value]); return; }
    const msg = value === undefined ? { method } : { method, value };
    try {
      this.iframe.contentWindow.postMessage(JSON.stringify(msg), VIMEO_ORIGIN);
    } catch (_) { /* iframe gone — ignore */ }
  }

  _onMessage(e) {
    if (e.origin !== VIMEO_ORIGIN || e.source !== this.iframe.contentWindow) return;
    let data;
    try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; }
    catch (_) { return; }

    if (data.event === 'ready') {
      this.ready = true;
      // subscribe to the events the feed UI needs
      ['play', 'pause', 'timeupdate', 'finish'].forEach(ev =>
        this._send('addEventListener', ev));
      this._send('getDuration');
      // flush anything issued before ready
      this.queued.forEach(([m, v]) => this._send(m, v));
      this.queued = [];
      return;
    }
    if (data.method === 'getDuration') this.duration = data.value || 0;

    switch (data.event) {
      case 'timeupdate': {
        const d = data.data || {};
        this.duration = d.duration || this.duration;
        this.callbacks.onTimeUpdate?.({
          seconds: d.seconds || 0,
          percent: d.percent || 0,
          duration: this.duration,
        });
        break;
      }
      case 'play':   this.callbacks.onPlay?.();  break;
      case 'pause':  this.callbacks.onPause?.(); break;
      case 'finish': this._send('play');         break; // belt & suspenders w/ loop=1
    }
  }

  /* ---- public controls (used by feed.js) ---- */
  play()          { this._send('play'); }
  pause()         { this._send('pause'); }
  setMuted(m)     { this._send('setVolume', m ? 0 : 1); }
  seekToPercent(p){
    if (this.duration) this._send('seekTo', this.duration * Math.min(Math.max(p, 0), 1));
  }

  destroy() {
    window.removeEventListener('message', this._onMessage);
    this.iframe.remove();
  }
}

/* Small shared helper */
function formatDuration(sec) {
  if (!sec && sec !== 0) return '';
  sec = Math.round(sec);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}
